import type { HelpStep } from "newui/components/ui/Help";

/**
 * Guided tour steps for the Quote Table editor modal.
 *
 * The steps walk a procurer through the major surfaces of the modal:
 * template selection, the column grid, header editing, the right-click
 * context menu, and finally name/description before submit.
 */
export const buildQuoteTableSteps = (): HelpStep[] => [
	{
		target: '[data-testid="spreadsheet-grid"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Design your quote table</h3>
				<p>
					This is the column layout your suppliers will see. Click a column
					header to edit it, right-click for more options, or drag the grip to
					reorder.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="quote-table-template"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Start from a template</h3>
				<p>
					Pick a preset to scaffold a common layout — Preliminaries, BoQ, or
					Materials — or pull in a template you saved earlier. Templates are
					optional; you can build a table from scratch.
				</p>
			</div>
		),
		placement: "bottom",
		disableBeacon: true,
	},
	{
		target: '[data-testid="ghost-add-column-cell"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Add columns</h3>
				<p>
					Use the trailing <strong>+</strong> cell to add a new column. Each
					column has a type (Description, Quantity, Value, Total, etc.) that
					controls how suppliers respond.
				</p>
			</div>
		),
		placement: "left",
		disableBeacon: true,
	},
	{
		target: '[data-testid="row-number-header"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Preview rows</h3>
				<p>
					The numbered rows below show sample values so you can see what the
					table will look like to suppliers. They&apos;re for preview only —
					suppliers fill in their own values when responding.
				</p>
			</div>
		),
		placement: "right",
		disableBeacon: true,
	},
	{
		target: '[data-testid="quote-table-name-input"]',
		content: (
			<div className="text-sm">
				<h3 className="mb-2 text-base font-semibold">Name and description</h3>
				<p>
					Give the table a clear name and a short description of what it
					covers. Both fields are required — the description helps suppliers
					understand what you&apos;re asking for.
				</p>
			</div>
		),
		placement: "top",
		disableBeacon: true,
	},
];
